/**
 * `NetworkRelay` — the lockstep client (U18, R27–R29).
 *
 * Implements the same `Relay` interface as `LocalRelay`, so nothing else in the
 * codebase knows which is active (KTD3). Commands submitted during tick N are
 * sent to the relay and executed at tick N+delay once the relay broadcasts the
 * ordered set.
 *
 * **A client without the committed set for the tick it wants stalls.** It never
 * guesses forward. Stalling is correct and visible; guessing is a desync.
 */

import type { Command, CommandWorld, Rejection } from './commands.js'
import { CommandRing, Scheduler, type Relay } from './relay.js'
import {
  MAX_CHAT_LENGTH,
  MSG,
  decodeCommands,
  decodeMessage,
  encodeCommands,
  encodeMessage,
  type ServerMessage,
} from './protocol.js'
import { HASH_INTERVAL, INPUT_DELAY_TICKS } from '../core/config.js'

/** A clock that does not depend on the DOM, so tests can run headless. */
const now = (): number =>
  typeof performance !== 'undefined' ? performance.now() : Date.now()

/** Milliseconds between keepalive pings. Well inside any idle timeout. */
const KEEPALIVE_MS = 15_000

/**
 * How many recent submissions are kept so they can be sent again.
 *
 * A relay that restarts loses the commands it had staged but not yet
 * committed, and it cannot invent them — only the clients still have them.
 * This is the buffer that lets it ask.
 *
 * The barrier bounds how far apart two clients can be: a tick commits only
 * once everyone has submitted for it, and a client submits for `tick + 1 +
 * delay` after executing `tick`, so nobody can be more than `delay + 1` ticks
 * ahead of anybody else. A handful would do; this is that with a wide margin,
 * and it is thirty-two short strings.
 */
const RESEND_WINDOW = 32

export interface NetworkRelayOptions {
  url: string
  code: string
  world: CommandWorld
  token?: string
  /** Injected so tests can drive a fake socket. */
  connect?: (url: string) => WebSocketLike
  onEvent?: (event: NetworkEvent) => void
}

export interface WebSocketLike {
  send(data: string): void
  close(code?: number, reason?: string): void
  onopen: ((ev: unknown) => void) | null
  onmessage: ((ev: { data: unknown }) => void) | null
  onclose: ((ev: unknown) => void) | null
  onerror: ((ev: unknown) => void) | null
}

export type NetworkEvent =
  | { kind: 'connected'; slot: number; token: string }
  | { kind: 'start'; slots: Array<{ faction: number; team: number }> }
  | { kind: 'stalled'; tick: number }
  | { kind: 'resumed'; tick: number }
  | { kind: 'late'; slot: number }
  | { kind: 'dropped'; slot: number; atTick: number; reason: string }
  | { kind: 'desync'; from: number; to: number }
  | { kind: 'error'; reason: string }
  | { kind: 'chat'; slot: number; text: string }
  | { kind: 'closed' }

export class NetworkRelay implements Relay {
  private readonly scheduler: Scheduler
  private socket: WebSocketLike | null = null
  private readonly onEvent: ((e: NetworkEvent) => void) | undefined

  /** Ticks whose sets have arrived from the relay. */
  private readonly committed = new CommandRing()
  /** Highest tick we have submitted for, so each tick is submitted exactly once. */
  private submittedThrough = -1
  private pending: Command[] = []
  private keepalive: number | null = null
  /** Recent submissions, kept so a restarted relay can ask for them again. */
  private readonly sent = new Map<number, string>()
  /** Highest tick the relay has sent us a set for. */
  private ackTick = -1
  /** Highest tick the simulation has actually run. */
  private executedThrough = -1
  /** True while re-opening a socket for a match already under way. */
  private resuming = false

  slot = -1
  token: string | null = null
  seed = ''
  mapSeed = ''
  /** Slots dropped by the relay, applied at the announced tick on every client. */
  readonly dropped = new Map<number, number>()

  private stalling = false
  /** Wall-clock time the last commit arrived, for the diagnostics panel. */
  private lastCommitAt = 0

  private lastHashSent = -1
  desyncWindow: { from: number; to: number } | null = null
  closed = false

  constructor(private readonly opts: NetworkRelayOptions) {
    this.scheduler = new Scheduler({ localSlot: -1, world: opts.world })
    this.onEvent = opts.onEvent
  }

  get localSlot(): number {
    return this.slot
  }

  /**
   * Hand the relay the simulation's own view of the world.
   *
   * Called by `Sim.create` through the relay factory, once the match exists.
   * Until then the relay is validating against the lobby's placeholder.
   */
  attach(world: CommandWorld): void {
    this.scheduler.attach(world, this.slot)
  }

  get delay(): number {
    return INPUT_DELAY_TICKS
  }

  connect(): void {
    const factory =
      this.opts.connect ??
      ((url: string) => new WebSocket(url) as unknown as WebSocketLike)
    const socket = factory(this.opts.url)
    this.socket = socket
    this.closed = false

    socket.onopen = () => {
      // The token we were given last time takes precedence over the one we
      // were constructed with: reclaiming a seat needs the token for *this*
      // match, and on a reconnect that is the one the relay issued us.
      const token = this.token ?? this.opts.token
      socket.send(
        encodeMessage({
          t: MSG.Join,
          code: this.opts.code,
          ...(token ? { token } : {}),
          ack: this.ackTick,
        }),
      )
      this.startKeepalive()
    }
    socket.onmessage = (ev) => this.onMessage(String(ev.data))
    socket.onclose = () => {
      this.closed = true
      this.stopKeepalive()
      this.onEvent?.({ kind: 'closed' })
    }
    socket.onerror = () => this.onEvent?.({ kind: 'error', reason: 'socket' })
  }

  /**
   * Re-open the socket for a match already in progress.
   *
   * Everything that makes this client itself — its slot, its token, the ticks
   * it has run, the commands it has sent — stays exactly as it was. Only the
   * transport is replaced. Building a fresh `NetworkRelay` instead would be
   * simpler and wrong: the running game holds a reference to this object, and
   * a new one would arrive with no history and no way to prove it owns the
   * seat.
   */
  reconnect(): void {
    if (!this.closed) return
    this.resuming = true
    this.connect()
  }

  /**
   * A ping every few seconds, for as long as the socket is open.
   *
   * A lobby waiting for its second player exchanges nothing, and an idle
   * connection gets closed somewhere between the browser and the relay. During
   * a match the tick traffic keeps it alive on its own; this covers the wait
   * before and any lull after.
   */
  /**
   * A snapshot for the diagnostics panel.
   *
   * Everything a stalled match needs to explain itself: which tick each side
   * has reached, whether we are waiting, and how long we have been waiting.
   * Without it a lockstep freeze is indistinguishable from a crash, which is
   * what "multiplayer feels finicky" usually turns out to mean.
   */
  diagnostics(): {
    slot: number
    tick: number
    committedThrough: number
    submittedThrough: number
    stalling: boolean
    sinceCommitMs: number
    closed: boolean
  } {
    return {
      slot: this.slot,
      tick: this.scheduler.currentTick,
      committedThrough: this.committed.highestCommitted,
      submittedThrough: this.submittedThrough,
      stalling: this.stalling,
      sinceCommitMs: this.lastCommitAt === 0 ? -1 : Math.round(now() - this.lastCommitAt),
      closed: this.closed,
    }
  }

  private startKeepalive(): void {
    this.stopKeepalive()
    if (typeof setInterval !== 'function') return
    this.keepalive = setInterval(() => {
      if (this.closed || !this.socket) return
      try {
        this.socket.send(encodeMessage({ t: MSG.Ping }))
      } catch {
        // The socket is on its way out; onclose will deal with it.
      }
    }, KEEPALIVE_MS) as unknown as number
  }

  private stopKeepalive(): void {
    if (this.keepalive !== null) {
      clearInterval(this.keepalive)
      this.keepalive = null
    }
  }

  private onMessage(raw: string): void {
    const msg = decodeMessage(raw) as ServerMessage | null
    if (!msg) return

    switch (msg.t) {
      case MSG.Welcome: {
        this.slot = msg.slot
        this.token = msg.token
        this.seed = msg.seed
        this.mapSeed = msg.mapSeed
        if (this.resuming) {
          this.resuming = false
          this.resend()
        }
        this.onEvent?.({ kind: 'connected', slot: msg.slot, token: msg.token })
        break
      }
      case MSG.Start:
        this.onEvent?.({ kind: 'start', slots: msg.slots })
        break
      case MSG.Commit: {
        const commands = decodeCommands(msg.cmds)
        if (!commands) {
          // A set we cannot decode is not a set we may guess at.
          this.fail('malformed-commit')
          return
        }
        this.lastCommitAt = now()
        this.ackTick = Math.max(this.ackTick, msg.tick)
        // A resumed match restarts at the tick the *furthest behind* client
        // reached, so anyone ahead is sent sets they have already run. They
        // are the same sets — a commit is a pure function of its
        // submissions — and replaying one would run the tick twice.
        if (msg.tick <= this.executedThrough) break
        this.committed.set(msg.tick, commands)
        if (this.stalling) {
          this.stalling = false
          this.onEvent?.({ kind: 'resumed', tick: msg.tick })
        }
        break
      }
      case MSG.Late:
        this.onEvent?.({ kind: 'late', slot: msg.slot })
        break
      case MSG.Drop:
        // Applied at the announced tick, so every remaining client applies it
        // identically. A drop applied on arrival would land on a different tick
        // on each client, which is a desync by another name.
        this.dropped.set(msg.slot, msg.atTick)
        this.onEvent?.({ kind: 'dropped', slot: msg.slot, atTick: msg.atTick, reason: msg.reason })
        break
      case MSG.Desync:
        this.desyncWindow = { from: msg.from, to: msg.to }
        this.onEvent?.({ kind: 'desync', from: msg.from, to: msg.to })
        this.close()
        break
      case MSG.Chat:
        this.onEvent?.({ kind: 'chat', slot: msg.slot ?? -1, text: msg.text })
        break
      case MSG.Error:
        this.onEvent?.({ kind: 'error', reason: msg.reason })
        break
      default:
        break
    }
  }

  private fail(reason: string): void {
    this.onEvent?.({ kind: 'error', reason })
    this.close()
  }

  /**
   * Queue a command for the next submission. It is sent when the engine
   * finishes the current tick, so one frame's worth of orders travels as one
   * message rather than as several.
   */
  submit(input: Omit<Command, 'slot' | 'seq'> & { slot?: number; seq?: number }): Rejection | null {
    if (this.slot < 0) return { reason: 'wrong-slot', detail: 'not connected' }
    const cmd = { ...input, slot: this.slot, seq: this.scheduler.nextSeq() } as Command
    const execTick = this.scheduler.currentTick + 1 + this.delay
    const rejection = this.scheduler.check(cmd, execTick, this.slot)
    if (rejection) return rejection
    this.pending.push(cmd)
    return null
  }

  commandsFor(tick: number): readonly Command[] | null {
    const set = this.committed.get(tick)
    if (set === null && !this.stalling) {
      this.stalling = true
      this.onEvent?.({ kind: 'stalled', tick })
    }
    return set
  }

  onTickExecuted(tick: number, hash: number | null): void {
    this.scheduler.currentTick = tick
    this.committed.release(tick - 1)

    // Submit exactly once per tick, including empty sets. An empty submission
    // is what tells the relay this client is alive — silence is what the
    // deadline path is watching for.
    //
    // The submission is for `tick + 1 + delay`, which is where the pending
    // commands were validated to execute, **not** for the tick just executed.
    // Submitting for the executed tick fills nothing: that tick is already
    // committed, so the barrier never advances and both clients stall four
    // ticks in.
    this.executedThrough = Math.max(this.executedThrough, tick)

    const target = tick + 1 + this.delay
    if (target > this.submittedThrough) {
      this.submittedThrough = target
      const cmds = encodeCommands(this.pending)
      this.remember(target, cmds)
      this.send({ t: MSG.Submit, tick: target, cmds })
      this.pending = []
    }

    if (hash !== null && tick > this.lastHashSent) {
      this.lastHashSent = tick
      this.send({ t: MSG.Hash, tick: tick + 1, hash })
    }
  }

  /**
   * Say something to the other players.
   *
   * Nothing is printed locally — the line comes back from the relay and is
   * printed then, so every client shows the same log in the same order. It
   * also means a message that never made it out never appears to have been
   * sent, which is the truthful thing to show.
   */
  sendChat(text: string): void {
    const trimmed = text.trim().slice(0, MAX_CHAT_LENGTH)
    if (trimmed.length === 0) return
    this.send({ t: MSG.Chat, text: trimmed })
  }

  /** Keep a submission around in case a restarted relay needs it again. */
  private remember(tick: number, cmds: string): void {
    this.sent.set(tick, cmds)
    for (const key of this.sent.keys()) {
      if (key <= tick - RESEND_WINDOW) this.sent.delete(key)
    }
  }

  /**
   * Send every buffered submission again.
   *
   * All of them, not just the ones past some estimate of where the relay is:
   * the relay works out where the match resumes from what every client
   * reports, and it does not know that yet at the moment this is sent. It
   * discards whatever turns out to be old.
   */
  private resend(): void {
    for (const [tick, cmds] of [...this.sent].sort((a, b) => a[0] - b[0])) {
      this.send({ t: MSG.Submit, tick, cmds })
    }
  }

  private send(msg: object): void {
    if (!this.socket || this.closed) return
    try {
      this.socket.send(encodeMessage(msg as never))
    } catch {
      this.closed = true
    }
  }

  /** True while waiting on a set. The HUD shows a reconnecting indicator. */
  get isStalled(): boolean {
    return this.stalling
  }

  close(): void {
    if (this.closed) return
    this.closed = true
    try {
      this.socket?.send(encodeMessage({ t: MSG.Leave }))
      this.socket?.close()
    } catch {
      // Already gone.
    }
  }

  dispose(): void {
    this.close()
    this.committed.clear()
  }
}

export { HASH_INTERVAL }
